import Button from "@components/ui/buttons/Button";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import Icon1 from "@assets/icons/Icon";
import { Text1, Text2, Text3 } from "@components/ui/texts/Texts";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useSearchParams } from "react-router-dom";

import DataGrid from "@components/ui/tables/DataGrid";
import { transformProjectsToTable } from "@components/ui/tables/transformToTables";
import { useDataStore } from "@store/user_org";
import { useProjectStore } from "@store/projectStore";
import { isGUEST } from "@helpers/accessHelpers";
import { isOwnerOrAdmin, canDeleteProject } from "@utils/permissions";
import { orgMembersToUsers } from "@components/ui/blocks/MemberAssignmentBlock";
import CheckBoxInput from "@components/ui/selection/CheckBoxInput";

import { CreateProjectModal } from "@components/modals/CreateProjectModal";
import ProjectsMapModal from "@components/modals/ProjectsMapModal";
import ConfirmationModal from "@components/support/ConfirmationModal";
import { showNotification } from "context/notification.service";
import { navigateTo } from "context/navigation.service";
import { searchProjectsFull } from "@services/algolia";

const statusTabs = [
  { value: "all", label: "Tous" },
  { value: "in_progress", label: "En cours" },
  { value: "delivered", label: "Livrés" },
  { value: "sav", label: "SAV" },
];

export default function ProjectsScreen() {
  const orgData = useDataStore((state) => state.orgData);
  const orgMembers = useDataStore((state) => state.orgMembers);
  const userData = useDataStore((state) => state.userData);
  const isGuest = isGUEST(userData);
  // Hide a member's own role filter: conducteurs don't need the Conducteur
  // filter, commerciaux don't need the Commercial filter.
  const isConductorRole = userData?.role === "conductor";
  const isCommercialRole = userData?.role === "commercial";
  const {
    projects,
    isLoadingProjects,
    hasMoreProjects,
    hasPrevProjects,
    fetchProjects,
    fetchNextPage,
    fetchPrevPage,
    setProjectsSort,
    removeProjects,
  } = useProjectStore();
  const [searchParams, setSearchParams] = useSearchParams();
  const [table, setTable] = useState<any>(null);
  const [createFolderOpen, setCreateFolderOpen] = useState<boolean>(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showMap, setShowMap] = useState(false);
  const [activeTab, setActiveTab] = useState<string>(
    searchParams.get("status") || "all",
  );
  const [filterOpen, setFilterOpen] = useState(false);
  const [filterCommercialIds, setFilterCommercialIds] = useState<string[]>([]);
  const [filterConductorIds, setFilterConductorIds] = useState<string[]>([]);
  const filterBtnRef = useRef<HTMLButtonElement>(null);
  const filterDropdownRef = useRef<HTMLDivElement>(null);
  const [filterPos, setFilterPos] = useState({ top: 0, left: 0 });

  const orgUsers = orgMembersToUsers(orgMembers);
  const commercials = orgMembers.filter(
    (m) => m.role === "commercial" && m.status !== "inactive",
  );
  const conductors = orgMembers.filter(
    (m) => m.role === "conductor" && m.status !== "inactive",
  );

  const hasActiveFilters =
    filterCommercialIds.length > 0 || filterConductorIds.length > 0;

  // Close filter dropdown on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        filterBtnRef.current?.contains(target) ||
        filterDropdownRef.current?.contains(target)
      )
        return;
      setFilterOpen(false);
    };
    if (filterOpen) {
      document.addEventListener("mousedown", handleClick);
      return () => document.removeEventListener("mousedown", handleClick);
    }
  }, [filterOpen]);

  // Update filter dropdown position when opening
  useEffect(() => {
    if (filterOpen && filterBtnRef.current) {
      const rect = filterBtnRef.current.getBoundingClientRect();
      setFilterPos({ top: rect.bottom + 4, left: rect.left });
    }
  }, [filterOpen]);

  // Fail-closed: only fetch once userData is loaded and we know the user's
  // role. Before that, `isGUEST(null)` returns false, which would briefly
  // render non-guest UI (including the SAV tab) to a guest user on refresh.
  const userLoaded = !!userData?.id;

  useEffect(() => {
    if (!userLoaded) return;
    // Non-guests: need orgData to be loaded (they belong to one org).
    // Guests: can belong to projects across multiple orgs; backend filters
    // purely by guest_user_ids on current_user.uid, so any org_id works as a
    // passthrough.
    // Commercial/conductor filters are applied server-side (see fetchProjects):
    // filtering the loaded page client-side only searched the current 50 rows,
    // so a commercial on projects beyond the first page appeared to have far
    // fewer than they actually do.
    const filters = { commercialIds: filterCommercialIds, conductorIds: filterConductorIds };
    if (isGuest) {
      fetchProjects(userData?.org_id || "", activeTab, filters);
    } else if (orgData?.id) {
      fetchProjects(orgData.id, activeTab, filters);
    }
  }, [userLoaded, isGuest, orgData?.id, userData?.org_id, activeTab, filterCommercialIds, filterConductorIds]);

  useEffect(() => {
    const table = transformProjectsToTable(
      projects,
      "all",
      orgData,
      "",
      orgUsers,
      userData?.id || "",
    );
    setTable(table);
  }, [orgData, projects, orgMembers, userData?.id]);

  const handleDeleteSelected = () => {
    removeProjects(selectedIds);
    showNotification({
      type: "success",
      message: `${selectedIds.length} projet(s) supprimé(s)`,
    });
    setSelectedIds([]);
    setShowDeleteConfirm(false);
  };

  return (
    <div className="h-screen flex flex-col overflow-hidden">
      {createFolderOpen && (
        <CreateProjectModal onClose={() => setCreateFolderOpen(false)} />
      )}
      {showMap && (
        <ProjectsMapModal onClose={() => setShowMap(false)} />
      )}
      {showDeleteConfirm && (
        <ConfirmationModal
          title={`Supprimer ${selectedIds.length} projet(s) ?`}
          message="Cette action est irréversible. Les projets et toutes leurs données seront définitivement supprimés."
          onClose={() => setShowDeleteConfirm(false)}
          onConfirm={handleDeleteSelected}
          confirmText="CONFIRMER"
        />
      )}

      {/* Header */}
      <div className="flex-shrink-0 pt-10 px-4 lg:px-10 pb-4">
        <div className="flex flex-col md:flex-row w-full max-w-1p5k justify-between">
          <div className="flex flex-col w-full md:w-400">
            <Text1>Projets</Text1>
            <Text2>Retrouvez tous vos projets ici.</Text2>
            <Text3>
              Les projets sont organisés par status et priorité. Vous pouvez
              utiliser les filtres pour affiner votre recherche.
            </Text3>
          </div>
          <div className="flex flex-row gap-2 items-center">
            {selectedIds.length > 0 && canDeleteProject(userData) && (
              <SecondaryButton
                startIcon={<Icon1 name="delete" size={18} />}
                onClick={() => setShowDeleteConfirm(true)}
              >
                {`Supprimer la sélection (${selectedIds.length})`}
              </SecondaryButton>
            )}
            <SecondaryButton
              startIcon={<Icon1 name="position" size={20} />}
              onClick={() => setShowMap(true)}
            >
              Carte
            </SecondaryButton>
            {!isGuest && (
              <Button
                label="Nouveau projet"
                onClick={() => setCreateFolderOpen(true)}
              />
            )}
          </div>
        </div>
      </div>

      {/* DataGrid area — fills remaining screen height */}
      <div className="flex-1 min-h-0 px-4 lg:px-10 pb-6 flex flex-col gap-3 w-full">
        {/* Show the skeleton only on first paint — once we have a table we
            keep it rendered across tab switches and refetches so the page
            doesn't flash to skeleton every time the user changes tab. The
            store updates `projects` atomically at the end of fetchProjects,
            so the table swaps cleanly without an empty intermediate state. */}
        {!userLoaded || !table ? (
          <div className="flex flex-col gap-2">
            {[...Array(6)].map((_, i) => (
              <div
                key={i}
                className="h-12 rounded-lg bg-neutral-100 animate-pulse"
              />
            ))}
          </div>
        ) : (
          <>
            {/* Thin top-loader bar that surfaces background refetches
                (tab switch, sort, page nav) without unmounting the table. */}
            <div className="relative h-0.5 max-w-1p5k w-full overflow-hidden">
              {isLoadingProjects && (
                <div className="absolute inset-0 bg-blue-500/70 animate-projects-loader" />
              )}
            </div>
            <div
              className={`flex-1 min-h-0 max-w-1p5k w-full transition-opacity duration-200 ${
                isLoadingProjects ? "opacity-60" : "opacity-100"
              }`}
            >
              {table && (
                <DataGrid
                  selectable={true}
                  table={table}
                  hide_lines={true}
                  // Match the server page size (PROJECTS_PAGE_SIZE) so the grid
                  // doesn't re-slice the loaded rows into smaller client pages —
                  // the whole fetched page shows at once, keeping the global
                  // alphabetical order continuous instead of restarting.
                  initialPageSize={250}
                  // Preserve the alphabetical ordering that
                  // transformProjectsToTable already applied upstream.
                  initial_sort_field={null}
                  initial_saved_columns_showed={{ id: false, status: false, creation_date: false }}
                  tabs={
                    isGuest
                      ? statusTabs.filter(
                          (t) => t.value !== "sav",
                        )
                      : statusTabs
                  }
                  activeTab={activeTab}
                  onTabChange={(tab) => {
                    setActiveTab(tab);
                    setSearchParams({});
                  }}
                  onClick={(item) =>
                    navigateTo({ route: "/project/" + item.id })
                  }
                  handleSelectionModelChange={(ids) => setSelectedIds(ids)}
                  onSearch={
                    orgData?.id && userData?.id
                      ? (q) => searchProjectsFull(q, orgData.id, {
                          userId: userData.id,
                          canViewAll: isOwnerOrAdmin(userData) || !!userData.config?.can_view_all_projects,
                        })
                      : undefined
                  }
                  onSearchSelect={(option) =>
                    navigateTo({ route: "/project/" + option.value })
                  }
                  hasNext={hasMoreProjects}
                  hasPrev={hasPrevProjects}
                  onNext={() => orgData?.id && fetchNextPage(orgData.id)}
                  onPrev={() => orgData?.id && fetchPrevPage(orgData.id)}
                  handleSortChange={(model) => {
                    const validSortFields = ["name", "city", "creation_date", "status"];
                    if (orgData?.id && model.length > 0 && validSortFields.includes(model[0].field))
                      setProjectsSort(
                        orgData.id,
                        model[0].field,
                        model[0].sort,
                      );
                  }}
                  storageKey="projects"
                  filterContent={
                    !isGuest ? (
                      <>
                        <button
                          ref={filterBtnRef}
                          onClick={() => setFilterOpen(!filterOpen)}
                          title="Filtrer les projets"
                          className={`inline-flex items-center gap-1 px-3 py-3 border rounded-xl text-sm transition-colors ${
                            hasActiveFilters
                              ? "border-black text-black bg-neutral-100 hover:bg-neutral-200"
                              : "border-neutral-300 text-neutral-500 hover:bg-neutral-50 hover:border-neutral-400 hover:text-neutral-700"
                          }`}
                        >
                          <Icon1
                            name="settings"
                            size={14}
                            color={hasActiveFilters ? "black" : "grey"}
                          />
                          Filtres
                          {hasActiveFilters && (
                            <span className="ml-1 bg-black text-white text-h5 rounded-full w-4 h-4 flex items-center justify-center">
                              {filterCommercialIds.length +
                                filterConductorIds.length}
                            </span>
                          )}
                        </button>
                        {filterOpen &&
                          createPortal(
                            <div
                              ref={filterDropdownRef}
                              style={{
                                position: "fixed",
                                top: filterPos.top,
                                left: filterPos.left,
                                zIndex: 9999,
                                maxHeight: `calc(100vh - ${filterPos.top + 16}px)`,
                                overflowY: "auto",
                              }}
                              className="bg-white border border-neutral-200 rounded-lg shadow-lg p-3 min-w-250"
                            >
                              {!isCommercialRole && (
                                <>
                                  <div className="text-h3 font-semibold text-neutral-600 mb-2">
                                    Commercial
                                  </div>
                                  {commercials.map((m) => (
                                    <div key={m.id} className="px-1 py-0.5">
                                      <CheckBoxInput
                                        label={m.infos?.display_name || ""}
                                        checked={filterCommercialIds.includes(
                                          m.id,
                                        )}
                                        onChange={() =>
                                          setFilterCommercialIds((prev) =>
                                            prev.includes(m.id)
                                              ? prev.filter((id) => id !== m.id)
                                              : [...prev, m.id],
                                          )
                                        }
                                      />
                                    </div>
                                  ))}
                                </>
                              )}
                              {!isCommercialRole && !isConductorRole && (
                                <div className="border-t border-neutral-200 my-2" />
                              )}
                              {!isConductorRole && (
                                <>
                                  <div className="text-h3 font-semibold text-neutral-600 mb-2">
                                    Conducteur
                                  </div>
                                  {conductors.map((m) => (
                                    <div key={m.id} className="px-1 py-0.5">
                                      <CheckBoxInput
                                        label={m.infos?.display_name || ""}
                                        checked={filterConductorIds.includes(
                                          m.id,
                                        )}
                                        onChange={() =>
                                          setFilterConductorIds((prev) =>
                                            prev.includes(m.id)
                                              ? prev.filter((id) => id !== m.id)
                                              : [...prev, m.id],
                                          )
                                        }
                                      />
                                    </div>
                                  ))}
                                </>
                              )}
                              {hasActiveFilters && (
                                <>
                                  <div className="border-t border-neutral-200 my-2" />
                                  <button
                                    onClick={() => {
                                      setFilterCommercialIds([]);
                                      setFilterConductorIds([]);
                                    }}
                                    className="text-h4 text-neutral-400 hover:text-neutral-600"
                                  >
                                    Réinitialiser les filtres
                                  </button>
                                </>
                              )}
                            </div>,
                            document.body,
                          )}
                      </>
                    ) : undefined
                  }
                />
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
